import type { LocaleBaseConfig } from '../types';
import { DATE_FORMAT_VARIANT } from './constants';

export const LOCALE_DATE_FORMATS = (
  is24Hour: boolean = false
): Record<string, LocaleBaseConfig> => {
  const base: LocaleBaseConfig = {
    ...DATE_FORMAT_VARIANT,
    TIME_FORMAT: 'standard',
    GMT_OFF_SET: 'gmtOffSet',
    TIME_FORMAT_12H: 'fixed12h',
    TIME_FORMAT_24H: 'fixed24h',
    TIME_FORMAT_12H_WITH_SECONDS: 'fixed12hWithSeconds',
    TIME_FORMAT_24H_WITH_SECONDS: 'fixed24hWithSeconds',
    is24HourFormat: is24Hour,
  };

  return {
    en: base,
    hr: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    cs: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    da: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd [den] D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    fi: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
      TIME_FORMAT: 'finnish',
    },
    fr: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    de: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
      TIME_FORMAT: 'germanTimeFormat',
    },
    el: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    hu: {
      ...base,
      GENERAL_DATE_FORMAT: 'YYYY. MMM D.',
      DATE_HEADER: 'YYYY. MMMM D. • dddd',
      MONTH_YEAR: 'YYYY. MMMM',
      FULL_MONTH_DAY: 'MMM D.',
      WEEKDAY_MONTH_DAY: 'YYYY. MMMM D., dddd',
      NUMERIC_DATE: 'YYYY/MM/DD',
    },
    it: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    nb: {
      ...base,
      GENERAL_DATE_FORMAT: 'D. MMM YYYY',
      DATE_HEADER: 'D. MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D. MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D. MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    pl: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    pt: {
      ...base,
      GENERAL_DATE_FORMAT: 'D [de] MMM [de] YYYY',
      DATE_HEADER: 'D [de] MMMM [de] YYYY • dddd',
      MONTH_YEAR: 'MMMM [de] YYYY',
      FULL_MONTH_DAY: 'D [de] MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D [de] MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    ro: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    es: {
      ...base,
      GENERAL_DATE_FORMAT: 'D [de] MMM [de] YYYY',
      DATE_HEADER: 'D [de] MMMM [de] YYYY • dddd',
      MONTH_YEAR: 'MMMM [de] YYYY',
      FULL_MONTH_DAY: 'D [de] MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D [de] MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    sv: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd D MMMM',
      NUMERIC_DATE: 'YYYY/MM/DD',
    },
    tr: {
      ...base,
      GENERAL_DATE_FORMAT: 'D MMM YYYY',
      DATE_HEADER: 'D MMMM YYYY • dddd',
      MONTH_YEAR: 'MMMM YYYY',
      FULL_MONTH_DAY: 'D MMMM',
      WEEKDAY_MONTH_DAY: 'dddd, D MMMM',
      NUMERIC_DATE: 'DD/MM/YYYY',
    },
    ar: { ...base },
    'zh-cn': { ...base },
    'zh-tw': { ...base },
    nl: { ...base },
    hi: { ...base },
    id: { ...base },
    ja: { ...base },
    ko: { ...base },
    lt: { ...base },
    no: { ...base },
    ru: { ...base },
    th: { ...base },
    vi: { ...base },
  };
};
